import { isLeft, isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import type { HubspotTicketDetailData } from '../types';

interface UseHubspotTicketAssignmentParams {
  ticketId: string;
  trpc: TRPC | null;
  data: HubspotTicketDetailData | null;
  refetchTicket: () => Promise<void>;
  onTicketUpdated?: () => Promise<void>;
}

interface UseHubspotTicketAssignmentResult {
  selectedAssignedUserId: string;
  isAssignmentSaving: boolean;
  assignmentError: string | null;
  setSelectedAssignedUserId: (userId: string) => void;
  /** Pass the new id to save in the same tick as the selection (auto-save). */
  handleSaveAssignment: (nextUserId?: string) => Promise<void>;
}

export function useHubspotTicketAssignment({
  ticketId,
  trpc,
  data,
  refetchTicket,
  onTicketUpdated,
}: UseHubspotTicketAssignmentParams): UseHubspotTicketAssignmentResult {
  const [selectedAssignedUserId, setSelectedAssignedUserId] = useState('');
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedAssignedUserId(data?.assignedUser?.id ?? '');
    setAssignmentError(null);
  }, [data?.assignedUser?.id, ticketId]);

  const handleSaveAssignment = async (
    nextUserId: string = selectedAssignedUserId,
  ) => {
    if (!trpc?.client) {
      setAssignmentError('TRPC context not initialized');
      return;
    }

    if ((data?.assignedUser?.id ?? '') === nextUserId) {
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError(null);

    try {
      const result = await trpc.client.assignHubspotTicket.mutate({
        ticketId,
        assignedUserId: nextUserId || null,
      });

      if (isRight(result)) {
        await Promise.all([refetchTicket(), onTicketUpdated?.()]);

        const nextAssignee = data?.assignableUsers.find(
          (user) => user.id === nextUserId,
        );
        toast.success(
          nextAssignee
            ? `Ticket assigned to ${nextAssignee.fullName}`
            : 'Ticket assignment cleared',
        );
      } else if (isLeft(result)) {
        setAssignmentError(
          result.left.message || 'Failed to update ticket assignment',
        );
        toast.error(
          result.left.message || 'Failed to update ticket assignment',
        );
      }
    } catch (err) {
      captureAndReportError('Error assigning HubSpot ticket:', err);
      setAssignmentError('Failed to update ticket assignment');
      toast.error('Failed to update ticket assignment');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  return {
    selectedAssignedUserId,
    isAssignmentSaving,
    assignmentError,
    setSelectedAssignedUserId,
    handleSaveAssignment,
  };
}
